import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Divider,
  Tooltip,
} from "@mui/material";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import LanOutlinedIcon from "@mui/icons-material/LanOutlined";
import LayersIcon from "@mui/icons-material/Layers";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MonitorOutlinedIcon from "@mui/icons-material/MonitorOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import PersonOutline from "@mui/icons-material/PersonOutline";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import ScienceIcon from "@mui/icons-material/Science";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

import { supabase } from "../../components/SupabaseClient";
import {
  ACCENT,
  ACCENT_TINT,
  ICON,
  INK_SECONDARY,
  INK_MUTED,
  HAIRLINE,
  HOVER,
} from "../../theme/tokens";

// Resource views
import ComputeResources from "./ComputeResources/ComputeResources";
import WorkflowCatalog from "./Workflow/WorkflowCatalog";
import Datasets from "./Datasets/Datasets";
import Models from "./Models/Models";
import StorageBrowser from "./StorageBrowser/StorageBrowser";

// Profile components
import Profile from "./MyProfile/Profile";
import MetadataView from "./SshKeys/MetadataView";

// Experiment components
import ExperimentsView from "./MyExperiments/ExperimentsView";
import CreateWorkspace from "./CreateExperiment/CreateWorkspace";
import SubmitJob from "./CreateExperiment/SubmitJob";

// Admin dashboard components
import DashboardClusterTopology from "./AdminDashboard/DashboardClusterTopology";
import DashboardClusterMonitor from "./AdminDashboard/DashboardClusterMonitor";
import DashboardStorageMonitor from "./AdminDashboard/DashboardStorageMonitor";
import DashboardExperimentMonitor from "./AdminDashboard/DashboardExperimentMonitor";
import DashboardModels from "./AdminDashboard/DashboardModels";
import DashboardUsers from "./AdminDashboard/DashboardUsers";
import AlertManager from "./AdminDashboard/AlertManager";
import MessagePage from "./AdminDashboard/MessagePage";

const HEADER_HEIGHT = 64;
const FOOTER_HEIGHT = 64;

const DRAWER_WIDTH_OPEN = 260;
const DRAWER_WIDTH_CLOSED = 70;

// Sidebar palette — sourced from the canonical design tokens.
const NAV_ACCENT = ACCENT;
const NAV_ACCENT_TINT = ACCENT_TINT;
const NAV_ICON = ICON;
const NAV_LABEL = INK_SECONDARY;
const NAV_SECTION = INK_MUTED;
const NAV_HAIRLINE = HAIRLINE;
const NAV_HOVER = HOVER;

// Persist each sidebar group's expanded/collapsed state across refreshes.
const SIDEBAR_GROUPS_KEY = "efabric_sidebar_groups";
const DEFAULT_OPEN_GROUPS = {
  admin: true,
  resources: true,
  experiments: true,
  settings: true,
};

function loadOpenGroups() {
  try {
    const raw = localStorage.getItem(SIDEBAR_GROUPS_KEY);
    return raw
      ? { ...DEFAULT_OPEN_GROUPS, ...JSON.parse(raw) }
      : { ...DEFAULT_OPEN_GROUPS };
  } catch {
    return { ...DEFAULT_OPEN_GROUPS };
  }
}

function saveOpenGroups(groups) {
  try {
    localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify(groups));
  } catch {
    // ignore storage errors
  }
}

function NavLeaf({ selected, onClick, icon: Icon, label, sidebarOpen }) {
  return (
    <Tooltip title={!sidebarOpen ? label : ""} placement="right" arrow>
      <ListItemButton
        onClick={onClick}
        selected={selected}
        disableRipple
        sx={{
          borderRadius: 2,
          mx: sidebarOpen ? 1 : "auto",
          mb: 0.25,
          py: 0.75,
          minHeight: 44,
          justifyContent: sidebarOpen ? "initial" : "center",
          px: sidebarOpen ? 1.5 : 1,
          width: sidebarOpen ? "auto" : "80%",
          color: NAV_LABEL,
          transition: "background-color 0.15s ease, color 0.15s ease",
          "& .MuiListItemIcon-root": {
            minWidth: 0,
            mr: sidebarOpen ? 1.5 : 0,
            justifyContent: "center",
            color: selected ? NAV_ACCENT : NAV_ICON,
            "& svg": { fontSize: 20 },
          },
          "&:hover": { bgcolor: NAV_HOVER },
          "&.Mui-selected": { bgcolor: NAV_ACCENT_TINT, color: NAV_ACCENT },
          "&.Mui-selected:hover": { bgcolor: NAV_ACCENT_TINT },
        }}
      >
        <ListItemIcon>
          <Icon />
        </ListItemIcon>
        <ListItemText
          primary={label}
          sx={{
            opacity: sidebarOpen ? 1 : 0,
            display: sidebarOpen ? "block" : "none",
            "& .MuiTypography-root": {
              fontSize: 13.5,
              fontWeight: selected ? 600 : 500,
              color: "inherit",
            },
          }}
        />
      </ListItemButton>
    </Tooltip>
  );
}

function NavGroup({ open, onToggle, label, sidebarOpen }) {
  // Collapsed rail: section headers reduce to a quiet hairline separator.
  if (!sidebarOpen) {
    return <Divider sx={{ my: 1, mx: 1.5, borderColor: NAV_HAIRLINE }} />;
  }

  return (
    <ListItemButton
      onClick={onToggle}
      disableRipple
      sx={{
        px: 2,
        py: 0.5,
        mt: 2,
        mb: 0.25,
        borderRadius: 1.5,
        "&:hover": { bgcolor: "transparent" },
      }}
    >
      <ListItemText
        primary={label}
        sx={{
          "& .MuiTypography-root": {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: NAV_SECTION,
          },
        }}
      />
      {open ? (
        <ExpandLess sx={{ color: NAV_SECTION, fontSize: "1.05rem" }} />
      ) : (
        <ExpandMore sx={{ color: NAV_SECTION, fontSize: "1.05rem" }} />
      )}
    </ListItemButton>
  );
}

const Workspace = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user?.userRole === "admin";
  const defaultView = isAdmin ? "admin/experiments" : "overview/compute";

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // --- Data State ---
  const [sshKeys, setSshKeys] = useState([]);
  const [sshKeyLoading, setSshKeyLoading] = useState(true);
  const [sshKeyLoadError, setSshKeyLoadError] = useState(null);

  const searchParams = new URLSearchParams(location.search);
  const initialView = searchParams.get("tab") || defaultView;
  const [view, setView] = useState(initialView);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const next = sp.get("tab") || defaultView;
    setView(next);
  }, [location.search, isAdmin]);

  useEffect(() => {
    if (!user) return;
    if (!("userRole" in user)) return;
    if (user.userRole !== "admin" && view.startsWith("admin/")) {
      setViewAndUrl("overview/compute");
    }
  }, [user, view]);

  const setViewAndUrl = (next) => {
    setView(next);
    const sp = new URLSearchParams(location.search);
    sp.set("tab", next);
    navigate(`/workspace?${sp.toString()}`, { replace: false });
  };

  // Restore the persisted group state (defaults to all-open, which keeps the
  // Admin group expanded once the async role resolves — no flicker on refresh).
  const [openGroup, setOpenGroup] = useState(loadOpenGroups);

  // Persist whenever a group is toggled.
  useEffect(() => {
    saveOpenGroups(openGroup);
  }, [openGroup]);

  const selectGroup = (key) => {
    if (!sidebarOpen) setSidebarOpen(true);
    setOpenGroup((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (user && user.id) {
      fetchSshKeys();
    } else {
      setSshKeys([]);
      setSshKeyLoading(false);
    }
  }, [user]);

  const fetchSshKeys = async () => {
    if (!user || !user.id) {
      setSshKeyLoadError("User not authenticated.");
      setSshKeyLoading(false);
      setSshKeys([]);
      return;
    }
    setSshKeyLoading(true);
    setSshKeyLoadError(null);
    try {
      const { data, error } = await supabase.rpc("get_user_public_keys", {
        p_user_id: user.id,
      });
      if (error) {
        setSshKeyLoadError(error.message || "Failed to load SSH keys.");
        setSshKeys([]);
      } else {
        setSshKeys(data || []);
      }
    } catch (err) {
      setSshKeyLoadError("Unexpected error fetching SSH keys.");
      setSshKeys([]);
    } finally {
      setSshKeyLoading(false);
    }
  };

  const handleCreationSuccess = () => {
    setViewAndUrl("experiments/list");
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          width: "100%",
          bgcolor: "#ffffffff",
          height: `calc(100vh - ${HEADER_HEIGHT}px - ${FOOTER_HEIGHT}px)`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: sidebarOpen ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED,
            flexShrink: 0,
            borderRight: `1px solid ${NAV_HAIRLINE}`,
            bgcolor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            transition: "width 0.3s ease",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 1,
              borderBottom: `1px solid ${NAV_HAIRLINE}`,
              flexShrink: 0,
            }}
          >
            <Tooltip
              title={sidebarOpen ? "Collapse" : "Expand"}
              placement="right"
            >
              <ListItemButton
                onClick={toggleSidebar}
                disableRipple
                sx={{
                  justifyContent: sidebarOpen ? "flex-start" : "center",
                  px: sidebarOpen ? 2 : 1,
                  borderRadius: 2,
                  color: NAV_ICON,
                  "&:hover": { bgcolor: NAV_HOVER },
                }}
              >
                {sidebarOpen ? <ChevronLeftIcon /> : <MenuOpenIcon />}
              </ListItemButton>
            </Tooltip>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              py: 1,
            }}
          >
            {/* Admin Dashboard */}
            {isAdmin && (
              <>
                <NavGroup
                  label="Admin Dashboard"
                  open={openGroup.admin}
                  onToggle={() => selectGroup("admin")}
                  sidebarOpen={sidebarOpen}
                />
                <Collapse in={openGroup.admin} unmountOnExit>
                  <NavLeaf
                    sidebarOpen={sidebarOpen}
                    selected={view === "admin/experiments"}
                    onClick={() => setViewAndUrl("admin/experiments")}
                    icon={ScienceIcon}
                    label="Experiment Monitor"
                  />
                  <NavLeaf
                    sidebarOpen={sidebarOpen}
                    selected={view === "admin/cluster"}
                    onClick={() => setViewAndUrl("admin/cluster")}
                    icon={QueryStatsOutlinedIcon}
                    label="Cluster Monitor"
                  />
                  <NavLeaf
                    sidebarOpen={sidebarOpen}
                    selected={view === "admin/storage"}
                    onClick={() => setViewAndUrl("admin/storage")}
                    icon={StorageOutlinedIcon}
                    label="Storage Monitor"
                  />
                  <NavLeaf
                    sidebarOpen={sidebarOpen}
                    selected={view === "admin/topology"}
                    onClick={() => setViewAndUrl("admin/topology")}
                    icon={LanOutlinedIcon}
                    label="Cluster Topology"
                  />
                  <NavLeaf
                    sidebarOpen={sidebarOpen}
                    selected={view === "admin/alerts"}
                    onClick={() => setViewAndUrl("admin/alerts")}
                    icon={NotificationsNoneOutlinedIcon}
                    label="Alerts"
                  />
                  <NavLeaf
                    sidebarOpen={sidebarOpen}
                    selected={view === "admin/models"}
                    onClick={() => setViewAndUrl("admin/models")}
                    icon={CloudDoneOutlinedIcon}
                    label="Models"
                  />
                  <NavLeaf
                    sidebarOpen={sidebarOpen}
                    selected={view === "admin/users"}
                    onClick={() => setViewAndUrl("admin/users")}
                    icon={Groups2OutlinedIcon}
                    label="Users"
                  />
                  <NavLeaf
                    sidebarOpen={sidebarOpen}
                    selected={view === "admin/messages"}
                    onClick={() => setViewAndUrl("admin/messages")}
                    icon={ForumOutlinedIcon}
                    label="Messages"
                  />
                </Collapse>
              </>
            )}

            {/* EFabric Catalog */}
            <NavGroup
              label="EFabric Catalog"
              open={openGroup.resources}
              onToggle={() => selectGroup("resources")}
              sidebarOpen={sidebarOpen}
            />
            <Collapse in={openGroup.resources} unmountOnExit>
              <NavLeaf
                sidebarOpen={sidebarOpen}
                selected={view === "overview/compute"}
                onClick={() => setViewAndUrl("overview/compute")}
                icon={DashboardIcon}
                label="Compute Resources"
              />
              <NavLeaf
                sidebarOpen={sidebarOpen}
                selected={view === "overview/workflows"}
                onClick={() => setViewAndUrl("overview/workflows")}
                icon={LayersIcon}
                label="Workflow Templates"
              />
              <NavLeaf
                sidebarOpen={sidebarOpen}
                selected={view === "overview/datasets"}
                onClick={() => setViewAndUrl("overview/datasets")}
                icon={FolderOutlined}
                label="Datasets"
              />
              <NavLeaf
                sidebarOpen={sidebarOpen}
                selected={view === "overview/models"}
                onClick={() => setViewAndUrl("overview/models")}
                icon={CloudDoneOutlinedIcon}
                label="Models"
              />
              <NavLeaf
                sidebarOpen={sidebarOpen}
                selected={view === "overview/storage"}
                onClick={() => setViewAndUrl("overview/storage")}
                icon={MonitorOutlinedIcon}
                label="Home Viewer"
              />
            </Collapse>

            {/* Experiments */}
            <NavGroup
              label="Experiments"
              open={openGroup.experiments}
              onToggle={() => selectGroup("experiments")}
              sidebarOpen={sidebarOpen}
            />
            <Collapse in={openGroup.experiments} unmountOnExit>
              <NavLeaf
                sidebarOpen={sidebarOpen}
                selected={view === "experiments/list"}
                onClick={() => setViewAndUrl("experiments/list")}
                icon={ScienceIcon}
                label="My Experiments"
              />
              <NavLeaf
                sidebarOpen={sidebarOpen}
                selected={view === "experiments/create"}
                onClick={() => setViewAndUrl("experiments/create")}
                icon={AddCircleOutlineIcon}
                label="Create a Workspace"
              />
              <NavLeaf
                sidebarOpen={sidebarOpen}
                selected={view === "jobs/submit"}
                onClick={() => setViewAndUrl("jobs/submit")}
                icon={AddCircleOutlineIcon}
                label="Submit a Job"
              />
            </Collapse>

            {/* Settings */}
            <NavGroup
              label="Settings"
              open={openGroup.settings}
              onToggle={() => selectGroup("settings")}
              sidebarOpen={sidebarOpen}
            />
            <Collapse in={openGroup.settings} unmountOnExit>
              <NavLeaf
                sidebarOpen={sidebarOpen}
                selected={view === "settings/profile"}
                onClick={() => setViewAndUrl("settings/profile")}
                icon={PersonOutline}
                label="My Profile"
              />
              <NavLeaf
                sidebarOpen={sidebarOpen}
                selected={view === "settings/ssh"}
                onClick={() => setViewAndUrl("settings/ssh")}
                icon={VpnKeyIcon}
                label="SSH Keys"
              />
            </Collapse>
          </Box>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            backgroundColor: (theme) => theme.palette.background.default,
            minWidth: 0,
            display: "flex",
            flexDirection: "row",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {/* Scrollable content area for the active workspace page. */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                flex: 1,
                pt: { xs: 3, sm: 4 },
                px: { xs: 2, sm: 3, md: 4 },
                pb: 6,
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {isAdmin && view === "admin/topology" && (
                <DashboardClusterTopology />
              )}
              {isAdmin && view === "admin/cluster" && (
                <DashboardClusterMonitor />
              )}
              {isAdmin && view === "admin/storage" && (
                <DashboardStorageMonitor />
              )}
              {isAdmin && view === "admin/experiments" && (
                <DashboardExperimentMonitor />
              )}
              {isAdmin && view === "admin/alerts" && <AlertManager />}
              {isAdmin && view === "admin/models" && <DashboardModels />}
              {isAdmin && view === "admin/users" && <DashboardUsers />}
              {isAdmin && view === "admin/messages" && (
                <MessagePage user={user} />
              )}

              {view === "overview/compute" && <ComputeResources />}
              {view === "overview/workflows" && <WorkflowCatalog />}
              {view === "overview/datasets" && <Datasets />}
              {view === "overview/models" && <Models />}
              {view === "overview/storage" && <StorageBrowser />}
              {view === "experiments/list" && (
                <ExperimentsView
                  key={user?.id}
                  onWorkspaceCreation={() =>
                    setViewAndUrl("experiments/create")
                  }
                  onJobSubmit={() => setViewAndUrl("jobs/submit")}
                />
              )}
              {view === "experiments/create" && (
                <CreateWorkspace
                  user={user}
                  onCancel={() => setViewAndUrl("experiments/list")}
                  onSuccess={handleCreationSuccess}
                  onGoToDatasets={() => setViewAndUrl("overview/datasets")}
                />
              )}

              {view === "jobs/submit" && (
                <SubmitJob
                  user={user}
                  onCancel={() => setViewAndUrl("experiments/list")}
                  onSuccess={() => setViewAndUrl("experiments/list")}
                  onGoToDatasets={() => setViewAndUrl("overview/datasets")}
                />
              )}

              {view === "settings/profile" && <Profile user={user} />}

              {view === "settings/ssh" && (
                <MetadataView
                  user={user}
                  sshKeyLoading={sshKeyLoading}
                  sshKeyLoadError={sshKeyLoadError}
                  sshKeys={sshKeys}
                  onSshKeyChange={fetchSshKeys}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Workspace;
